import React from 'react';

import { cn, Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@crossnokaye/ux-common';

import { useIsTruncated } from '@/hooks/use-is-truncated';

export type KeyValueLabelVariant = 'transparent' | 'block';
export type KeyValueLabelSize = 'xs' | 'sm' | 'md' | 'lg';

export interface KeyValueLabelProps {
  /** The key/label text to display on the left */
  keyText: string;
  /** The value text to display on the right */
  value: React.ReactElement | string | (() => React.ReactNode);
  /** Visual variant of the component */
  variant?: KeyValueLabelVariant;
  /** Additional CSS classes */
  className?: string;
  /** The size of the component */
  size?: KeyValueLabelSize;
  /** Optional tooltip text for the value side */
  valueTitle?: string;
}

type Variant = {
  container: string;
  key: string;
  value: string;
};

const variants: Record<KeyValueLabelVariant, Variant> = {
  transparent: {
    container: 'bg-transparent',
    key: 'text-muted-foreground',
    value: 'text-foreground',
  },
  block: {
    container: 'bg-card px-3 py-2 rounded-sm',
    key: 'text-muted-foreground',
    value: 'text-foreground',
  },
};

const sizeVariants: Record<KeyValueLabelSize, { key: string; value: string }> = {
  xs: {
    key: 'text-xs',
    value: 'text-xs',
  },
  sm: {
    key: 'text-sm',
    value: 'text-sm',
  },
  md: {
    key: 'text-md',
    value: 'text-md',
  },
  lg: {
    key: 'text-lg',
    value: 'text-lg',
  },
};

// TODO: migrate this to ux-common after we finish building out the equipment browser animatic cards that also contain the key value labels
export const KeyValueLabel: React.FC<KeyValueLabelProps> = ({
  keyText,
  value,
  variant = 'transparent',
  className,
  size = 'sm',
  valueTitle,
}) => {
  const variantStyles = variants[variant];
  const resolvedValueTitle = valueTitle ?? (typeof value === 'string' ? value : undefined);
  const { elementRef: valueRef, isTruncated: isValueTruncated } = useIsTruncated<HTMLSpanElement>({
    enabled: !!resolvedValueTitle,
    targetSelector: '[data-truncation-target="true"]',
    deps: [resolvedValueTitle],
  });
  const showValueTooltip = isValueTruncated && !!resolvedValueTitle;

  return (
    <div
      className={cn(
        'flex min-w-0 items-center justify-between gap-2',
        variantStyles.container,
        className
      )}
    >
      <span
        className={cn(
          'font-medium min-w-0 max-w-[55%] flex-shrink-0 truncate',
          variantStyles.key,
          sizeVariants[size].key
        )}
        title={keyText}
      >
        {keyText}
      </span>
      {/* Keep explicit tooltip primitives here: we need truncation-gated rendering and content-level event suppression to prevent parent click-through. */}
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild disabled={!showValueTooltip}>
          <span
            ref={valueRef}
            className={cn(
              'font-normal min-w-0 flex-1 truncate text-right',
              showValueTooltip && 'cursor-help',
              variantStyles.value,
              sizeVariants[size].value
            )}
          >
            {typeof value === 'function' ? value() : value}
          </span>
        </TooltipTrigger>
        {showValueTooltip ? (
          <TooltipPortal>
            <TooltipContent
              className="pointer-events-auto"
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              {resolvedValueTitle}
            </TooltipContent>
          </TooltipPortal>
        ) : null}
      </Tooltip>
    </div>
  );
};

KeyValueLabel.displayName = 'KeyValueLabel';
